/* Kharzat custom tasbih builder.
   Reads config + variant map from JSON script tags rendered by
   sections/custom-builder.liquid, drives the stepper, validates
   material -> colour -> karkusha compatibility, redraws the live
   SVG preview and posts to the cart with line item properties. */

class KzBuilder extends HTMLElement {
  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-kz-config]').textContent);
    this.variants = JSON.parse(this.querySelector('[data-kz-variants]').textContent);
    this.locale = document.documentElement.lang.split('-')[0] || 'en';
    this.strings = JSON.parse(this.querySelector('[data-kz-strings]').textContent);

    this.state = {
      material: null,
      beadColor: null,
      length: '33',
      diameter: '6mm',
      threadMaterial: null,
      threadColor: null,
      karkusha: null,
      divider: 'no',
      separatorShape: null,
    };

    this.steps = Array.from(this.querySelectorAll('.kz-step'));
    this.priceEl = this.querySelector('[data-kz-price]');
    this.submitBtn = this.querySelector('[data-kz-submit]');
    this.errorEl = this.querySelector('[data-kz-error]');
    this.previewEl = this.querySelector('[data-kz-preview]');
    this.variantInput = this.querySelector('[data-kz-variant-id]');

    ['material', 'beadColor', 'length', 'diameter', 'threadMaterial', 'threadColor', 'karkusha', 'divider', 'separatorShape']
      .forEach((step) => this.renderStep(step));
    this.updateAll();

    this.submitBtn.addEventListener('click', (e) => this.addToCart(e));
  }

  t(labelObj) {
    if (typeof labelObj === 'string') return labelObj;
    return labelObj[this.locale] || labelObj.en;
  }

  /* ------------ option data per step ------------ */

  optionsFor(step) {
    const c = this.config;
    switch (step) {
      case 'material':
        return c.materials.map((m) => ({ id: m.id, label: this.t(m.label) }));
      case 'beadColor': {
        const mat = c.materials.find((m) => m.id === this.state.material);
        return (mat ? mat.colors : []).map((col) => ({ id: col.id, label: this.t(col.label), hex: col.hex }));
      }
      case 'length':
        return c.lengths.map((l) => ({ id: l.id, label: this.t(l.label) }));
      case 'diameter':
        return c.diameters.map((d) => ({ id: d.id, label: this.t(d.label) }));
      case 'threadMaterial':
        return c.threadMaterials.map((tm) => ({ id: tm.id, label: this.t(tm.label) }));
      case 'threadColor':
        return c.threadColors.map((tc) => ({ id: tc.id, label: this.t(tc.label), hex: tc.hex }));
      case 'karkusha':
        return c.karkusha.map((k) => ({
          id: k.id,
          label: this.t(k.label),
          disabled: this.state.beadColor && k.colors && !k.colors.includes(this.state.beadColor),
        }));
      case 'divider':
        return [
          { id: 'no', label: this.strings.no },
          { id: 'yes', label: this.strings.yes },
        ];
      case 'separatorShape':
        return c.separatorShapes.map((s) => ({ id: s.id, label: this.t(s.label) }));
    }
    return [];
  }

  /* ------------ rendering ------------ */

  renderStep(step) {
    const stepEl = this.querySelector(`[data-kz-step="${step}"]`);
    if (!stepEl) return;
    const grid = stepEl.querySelector('.kz-options');
    grid.innerHTML = '';
    this.optionsFor(step).forEach((opt) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kz-option' + (opt.hex ? ' kz-option--swatch' : '');
      btn.dataset.value = opt.id;
      btn.setAttribute('aria-pressed', String(this.state[step] === opt.id));
      if (opt.disabled) btn.disabled = true;
      if (opt.hex) {
        const sw = document.createElement('span');
        sw.className = 'kz-swatch';
        sw.style.background = `radial-gradient(circle at 35% 30%, ${this.lighten(opt.hex, 45)}, ${opt.hex} 60%, ${this.darken(opt.hex, 30)})`;
        btn.appendChild(sw);
      }
      btn.appendChild(document.createTextNode(opt.label));
      const delta = this.deltaLabel(step, opt.id);
      if (delta) {
        const d = document.createElement('span');
        d.className = 'kz-option__delta';
        d.textContent = delta;
        btn.appendChild(d);
      }
      btn.addEventListener('click', () => this.select(step, opt.id));
      grid.appendChild(btn);
    });
  }

  select(step, value) {
    this.state[step] = value;

    // Downstream resets when an upstream choice invalidates them
    if (step === 'material') {
      this.state.beadColor = null;
      this.state.karkusha = null;
      this.renderStep('beadColor');
      this.renderStep('karkusha');
    }
    if (step === 'beadColor') {
      const k = this.config.karkusha.find((x) => x.id === this.state.karkusha);
      if (k && k.colors && !k.colors.includes(value)) this.state.karkusha = null;
      this.renderStep('karkusha');
    }
    if (step === 'divider') {
      if (value === 'no') this.state.separatorShape = null;
      this.renderStep('separatorShape');
      this.querySelector('[data-kz-step="separatorShape"]').hidden = value !== 'yes';
    }

    // variant axes affect each other's price deltas — refresh all three
    if (['material', 'length', 'diameter'].includes(step)) {
      ['material', 'length', 'diameter'].forEach((s) => this.renderStep(s));
    } else {
      this.renderStep(step);
    }
    this.updateAll();

    // auto-advance: open the next incomplete step
    const idx = this.steps.findIndex((s) => s.dataset.kzStep === step);
    for (let i = idx + 1; i < this.steps.length; i++) {
      const next = this.steps[i];
      if (next.hidden) continue;
      const key = next.dataset.kzStep;
      if (key && !this.state[key]) {
        this.steps[idx].removeAttribute('open');
        next.setAttribute('open', '');
        break;
      }
    }
  }

  updateAll() {
    // step headers: completion state + chosen value
    this.steps.forEach((stepEl) => {
      const key = stepEl.dataset.kzStep;
      if (!key) return;
      const chosen = this.state[key];
      const valEl = stepEl.querySelector('.kz-step__value');
      if (chosen) {
        stepEl.setAttribute('data-complete', '');
        const opt = this.optionsFor(key).find((o) => o.id === chosen);
        valEl.textContent = opt ? opt.label : '';
      } else {
        stepEl.removeAttribute('data-complete');
        valEl.textContent = '';
      }
    });

    // price from variant map
    const variant = this.currentVariant();
    if (variant) {
      this.priceEl.textContent = variant.price_formatted;
      this.variantInput.value = variant.id;
    } else {
      this.priceEl.textContent = this.strings.select_options;
      this.variantInput.value = '';
    }

    this.submitBtn.disabled = !this.isComplete();
    this.drawPreview();
  }

  currentVariant() {
    const { material, length, diameter } = this.state;
    if (!material || !length || !diameter) return null;
    return this.variants.find(
      (v) => v.material === material && v.length === length && v.diameter === diameter && v.available
    );
  }

  /* Price of the variant that WOULD be selected if `step` were set to `value`. */
  candidateVariant(step, value) {
    const sel = { material: this.state.material, length: this.state.length, diameter: this.state.diameter };
    sel[step] = value;
    if (!sel.material || !sel.length || !sel.diameter) return null;
    return this.variants.find(
      (v) => v.material === sel.material && v.length === sel.length && v.diameter === sel.diameter && v.available
    );
  }

  formatAmount(cents) {
    const units = cents / 100;
    const rounded = Math.round(units * 100) / 100;
    const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
    return `${this.strings.currency} ${text}`;
  }

  /* "+AED 190" / "−AED 120" relative to the current selection, or
     "from AED 280" when nothing is selected yet for this axis. */
  deltaLabel(step, value) {
    if (!['material', 'length', 'diameter'].includes(step)) return null;
    if (this.state[step] === value) return null;
    const candidate = this.candidateVariant(step, value);
    if (!candidate) return null;
    const current = this.currentVariant();
    if (!current) return `${this.strings.from} ${this.formatAmount(candidate.price_cents)}`;
    const delta = candidate.price_cents - current.price_cents;
    if (delta === 0) return null;
    const sign = delta > 0 ? '+' : '−';
    return `${sign}${this.formatAmount(Math.abs(delta))}`;
  }

  isComplete() {
    const s = this.state;
    const base =
      s.material && s.beadColor && s.length && s.diameter && s.threadMaterial && s.threadColor && s.karkusha;
    const dividerOk = s.divider === 'no' || (s.divider === 'yes' && s.separatorShape);
    return Boolean(base && dividerOk && this.currentVariant());
  }

  /* ------------ live SVG preview ------------ */

  colorHex(step, id, fallback) {
    const opt = this.optionsFor(step).find((o) => o.id === id);
    return (opt && opt.hex) || fallback;
  }

  drawPreview() {
    const beadHex = this.state.beadColor
      ? this.colorHex('beadColor', this.state.beadColor, '#C58230')
      : '#D8CBB4';
    const threadHex = this.state.threadColor
      ? this.colorHex('threadColor', this.state.threadColor, '#6E5637')
      : '#6E5637';
    const is99 = this.state.length === '99';
    const isThick = this.state.diameter === '10mm';
    const N = is99 ? 66 : 30; // display density, not literal count
    const R = is99 ? 190 : 165;
    const r = (isThick ? 15 : 11) * (is99 ? 0.72 : 1);
    const C = 250;

    let s = `<svg viewBox="0 0 500 560" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${this.strings.preview_alt}">`;
    s += `<defs>
      <radialGradient id="kzb" cx="0.35" cy="0.3" r="0.95">
        <stop offset="0%" stop-color="${this.lighten(beadHex, 38)}"/>
        <stop offset="55%" stop-color="${beadHex}"/>
        <stop offset="100%" stop-color="${this.darken(beadHex, 32)}"/>
      </radialGradient>
      <radialGradient id="kzd" cx="0.35" cy="0.3" r="0.95">
        <stop offset="0%" stop-color="${this.lighten(threadHex, 30)}"/>
        <stop offset="100%" stop-color="${this.darken(threadHex, 18)}"/>
      </radialGradient>
    </defs>`;
    s += `<circle cx="${C}" cy="${C}" r="${R}" fill="none" stroke="${threadHex}" stroke-width="3"/>`;

    const dividerAt = new Set();
    if (this.state.divider === 'yes') {
      const marks = is99 ? [Math.round(N / 3), Math.round((2 * N) / 3)] : [Math.round(N / 3), Math.round((2 * N) / 3)];
      marks.forEach((m) => dividerAt.add(m));
    }

    for (let i = 1; i < N; i++) {
      // i=0 (bottom of the ring) is where the imame sits
      const a = -Math.PI / 2 + (i / N) * 2 * Math.PI;
      const x = C + R * Math.cos(a);
      const y = C - R * Math.sin(a);
      if (dividerAt.has(i)) {
        const shape = this.state.separatorShape;
        if (shape === 'cube') {
          s += `<rect x="${x - r}" y="${y - r * 1.5}" width="${r * 2}" height="${r * 3}" fill="url(#kzd)" transform="rotate(${(-a * 180) / Math.PI + 90} ${x} ${y})"/>`;
        } else {
          s += `<ellipse cx="${x}" cy="${y}" rx="${r * 0.85}" ry="${r * 1.7}" fill="url(#kzd)" transform="rotate(${(-a * 180) / Math.PI + 90} ${x} ${y})"/>`;
        }
      } else {
        s += `<circle cx="${x}" cy="${y}" r="${r}" fill="url(#kzb)"/>`;
        s += `<ellipse cx="${x - r * 0.3}" cy="${y - r * 0.35}" rx="${r * 0.26}" ry="${r * 0.16}" fill="#fff" opacity="0.35"/>`;
      }
    }

    // imame at bottom + tassel in thread colour
    const iy = C + R;
    s += `<ellipse cx="${C}" cy="${iy + 28}" rx="${r * 0.9}" ry="${r * 2.4}" fill="url(#kzb)"/>`;
    const ty = iy + 28 + r * 2.4 + 8;
    s += `<circle cx="${C}" cy="${ty}" r="${r * 0.7}" fill="${threadHex}"/>`;
    const strands = this.state.karkusha === 'triple-strand' ? 3 : this.state.karkusha === 'royal-kazaz' ? 7 : 5;
    for (let k = 0; k < strands; k++) {
      const off = (k - (strands - 1) / 2) * 0.16;
      const ex = C + 90 * Math.sin(off);
      const ey = ty + 92 * Math.cos(off * 0.9);
      s += `<path d="M ${C} ${ty} Q ${C + 40 * Math.sin(off)} ${ty + 50}, ${ex} ${ey}" stroke="${threadHex}" stroke-width="2.6" fill="none" opacity="0.9"/>`;
      if (this.state.karkusha === 'silver-crown') {
        s += `<circle cx="${ex}" cy="${ey}" r="3.4" fill="#B9BDC4"/>`;
      }
    }
    s += `</svg>`;
    this.previewEl.innerHTML = s;
  }

  lighten(hex, pct) { return this.shade(hex, pct); }
  darken(hex, pct) { return this.shade(hex, -pct); }
  shade(hex, pct) {
    const n = parseInt(hex.slice(1), 16);
    const amt = Math.round(2.55 * pct);
    const r = Math.min(255, Math.max(0, (n >> 16) + amt));
    const g = Math.min(255, Math.max(0, ((n >> 8) & 0xff) + amt));
    const b = Math.min(255, Math.max(0, (n & 0xff) + amt));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  }

  /* ------------ cart ------------ */

  propertyLabel(key) {
    return this.strings.properties[key] || key;
  }

  async addToCart(e) {
    e.preventDefault();
    if (!this.isComplete()) return;
    const s = this.state;
    const label = (step) => {
      const opt = this.optionsFor(step).find((o) => o.id === s[step]);
      return opt ? opt.label : '';
    };

    const properties = {
      [this.propertyLabel('bead_color')]: label('beadColor'),
      [this.propertyLabel('thread_material')]: label('threadMaterial'),
      [this.propertyLabel('thread_color')]: label('threadColor'),
      [this.propertyLabel('karkusha')]: label('karkusha'),
      [this.propertyLabel('divider')]: s.divider === 'yes' ? this.strings.yes : this.strings.no,
    };
    if (s.divider === 'yes') {
      properties[this.propertyLabel('separator_shape')] = label('separatorShape');
    }

    this.submitBtn.setAttribute('aria-busy', 'true');
    this.errorEl.removeAttribute('data-visible');
    try {
      const res = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(this.variantInput.value), quantity: 1, properties }),
      });
      if (!res.ok) throw new Error((await res.json()).description || 'error');
      window.location.href = window.routes.cart_url;
    } catch (err) {
      this.errorEl.textContent = this.strings.cart_error;
      this.errorEl.setAttribute('data-visible', '');
    } finally {
      this.submitBtn.removeAttribute('aria-busy');
    }
  }
}

customElements.define('kz-builder', KzBuilder);
